import { useEffect, useMemo, useState } from 'react';
import { Link, useBlocker, useParams } from 'react-router-dom';
import { ArrowLeft, Globe, Info, Loader2, Lock, MapPin, Pencil, Search } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { usePermissions } from '@/hooks/usePermissions';
import * as repo from '../data/rolesRepository';
import { PermissionMatrix } from '../components/PermissionMatrix';
import { RoleFormDialog } from '../components/RoleFormDialog';
import {
  usePermissionCatalog,
  useRoleAssignments,
  useRolePermissionCodes,
  useRolesMutation,
  useRolesOverview,
} from '../hooks/useRoles';
import type { RoleAssignment, RoleFormValues } from '../types';
import { buildMatrix, diffCodes, matchesSearch } from '../utils/matrix';

export function RoleDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { can } = usePermissions();
  const canSeeUsers = can('user.manage');

  const { data: roles = [], isLoading: loadingRoles } = useRolesOverview();
  const { data: catalog = [], isLoading: loadingCatalog } = usePermissionCatalog();
  const { data: savedCodes, isLoading: loadingCodes } = useRolePermissionCodes(id);
  const { data: assignments = [], isLoading: loadingAssignments } = useRoleAssignments(id, canSeeUsers);

  const role = roles.find((r) => r.id === id);
  const locked = !!role?.permissions_locked;

  const matrix = useMemo(() => buildMatrix(catalog), [catalog]);
  const allResources = useMemo(() => matrix.flatMap((m) => m.resources), [matrix]);
  // A locked role holds the whole catalog by definition
  const saved = useMemo(
    () => new Set(locked ? catalog.map((p) => p.code) : savedCodes ?? []),
    [locked, catalog, savedCodes],
  );

  // null = no local edits (shows the saved set)
  const [draft, setDraft] = useState<Set<string> | null>(null);
  const selected = draft ?? saved;
  const { added, removed } = diffCodes(saved, selected);
  const dirty = added.length + removed.length > 0;

  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(false);

  const visibleModules = useMemo(
    () =>
      matrix
        .map((m) => ({ ...m, resources: m.resources.filter((r) => matchesSearch(r, m.module, search)) }))
        .filter((m) => m.resources.length > 0),
    [matrix, search],
  );

  const save = useRolesMutation((codes: string[]) => repo.setRolePermissions(id as string, codes), {
    success: 'Permisos guardados',
  });
  const update = useRolesMutation((values: RoleFormValues) => repo.updateRole(id as string, values), {
    success: 'Rol actualizado',
  });

  // Unsaved changes: block in-app navigation and warn on tab close / reload
  const blocker = useBlocker(({ currentLocation, nextLocation }) => dirty && currentLocation.pathname !== nextLocation.pathname);
  useEffect(() => {
    if (!dirty) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirty]);

  if (loadingRoles || loadingCatalog || loadingCodes) {
    return <div className="flex justify-center py-12"><Loader2 className="size-6 animate-spin text-muted-foreground" /></div>;
  }

  if (!role) {
    return (
      <div className="space-y-4 p-4 md:p-6">
        <BackLink />
        <Card><CardContent className="py-12 text-center text-sm text-muted-foreground">No se encontró el rol.</CardContent></Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 md:p-6">
      <div className="space-y-3">
        <BackLink />
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="space-y-1">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-2xl font-bold">{role.name}</h1>
              {locked && <Badge variant="secondary" className="gap-1"><Lock className="size-3" /> Acceso total</Badge>}
              {role.is_system && !locked && <Badge variant="secondary">Sistema</Badge>}
              {!role.is_active && <Badge variant="outline">Inactivo</Badge>}
            </div>
            <p className="font-mono text-xs text-muted-foreground">{role.code}</p>
            {role.description && <p className="text-sm text-muted-foreground">{role.description}</p>}
          </div>
          <Button variant="outline" onClick={() => setEditing(true)}><Pencil className="mr-2 size-4" /> Editar datos</Button>
        </div>
      </div>

      <Tabs defaultValue="permissions">
        <TabsList>
          <TabsTrigger value="permissions">Permisos ({selected.size})</TabsTrigger>
          <TabsTrigger value="users">Usuarios ({role.user_count})</TabsTrigger>
        </TabsList>

        <TabsContent value="permissions" className="space-y-4">
          {locked ? (
            <Alert>
              <Lock className="size-4" />
              <AlertDescription>
                Este rol tiene siempre todos los permisos, también los que se agreguen en el futuro. No se puede modificar.
              </AlertDescription>
            </Alert>
          ) : role.is_system ? (
            <Alert>
              <Info className="size-4" />
              <AlertDescription>
                Rol de sistema: el sistema lo usa (por ejemplo en eventos o votaciones). Puedes ajustar sus permisos, pero no eliminarlo.
              </AlertDescription>
            </Alert>
          ) : null}

          <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
            <div className="relative w-full max-w-sm">
              <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                className="pl-9"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar módulo, recurso o acción"
                aria-label="Buscar permisos"
              />
            </div>
            <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
              <span className="flex items-center gap-1.5"><MapPin className="size-3.5" /> Por territorio</span>
              <span className="flex items-center gap-1.5"><Globe className="size-3.5" /> Global (todo el país)</span>
              <span>Marcar cualquier acción incluye «Ver».</span>
            </div>
          </div>

          {visibleModules.length === 0 ? (
            <Card><CardContent className="py-12 text-center text-sm text-muted-foreground">Ningún permiso coincide con «{search}».</CardContent></Card>
          ) : (
            <PermissionMatrix
              modules={visibleModules}
              allResources={allResources}
              selected={selected}
              saved={saved}
              disabled={locked || save.isPending}
              onChange={setDraft}
            />
          )}
        </TabsContent>

        <TabsContent value="users">
          {!canSeeUsers ? (
            <Card><CardContent className="py-12 text-center text-sm text-muted-foreground">Necesitas el permiso de administrar usuarios para ver quién tiene este rol.</CardContent></Card>
          ) : loadingAssignments ? (
            <div className="flex justify-center py-12"><Loader2 className="size-6 animate-spin text-muted-foreground" /></div>
          ) : (
            <AssignmentsTable assignments={assignments} />
          )}
        </TabsContent>
      </Tabs>

      {dirty && (
        <div className="sticky bottom-4 z-30 rounded-lg border bg-card/95 shadow-lg backdrop-blur">
          <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
            <p className="text-sm" role="status">
              <span className="font-medium">Cambios sin guardar:</span>{' '}
              {added.length > 0 && <span className="text-success">+{added.length} </span>}
              {removed.length > 0 && <span className="text-destructive">−{removed.length}</span>}
            </p>
            <div className="flex gap-2">
              <Button variant="outline" disabled={save.isPending} onClick={() => setDraft(null)}>Descartar</Button>
              <Button disabled={save.isPending} onClick={() => save.mutate([...selected], { onSuccess: () => setDraft(null) })}>
                {save.isPending && <Loader2 className="mr-2 size-4 animate-spin" />}
                Guardar permisos
              </Button>
            </div>
          </div>
        </div>
      )}

      <RoleFormDialog
        mode={editing ? { kind: 'edit', role } : null}
        onOpenChange={(open) => !open && setEditing(false)}
        busy={update.isPending}
        onSubmit={(values) => update.mutate(values, { onSuccess: () => setEditing(false) })}
      />

      <AlertDialog open={blocker.state === 'blocked'}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Salir sin guardar?</AlertDialogTitle>
            <AlertDialogDescription>
              Tienes {added.length + removed.length} cambio(s) de permisos sin guardar. Si sales, se perderán.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => blocker.reset?.()}>Seguir editando</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => blocker.proceed?.()}
            >
              Salir sin guardar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function BackLink() {
  return (
    <Link to="/roles" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
      <ArrowLeft className="size-4" /> Roles
    </Link>
  );
}

const SCOPE_LABEL = { NATIONAL: 'Nacional', DISTRICT: 'Distrito', REGION: 'Región' } as const;

function AssignmentsTable({ assignments }: { assignments: RoleAssignment[] }) {
  if (assignments.length === 0) {
    return <Card><CardContent className="py-12 text-center text-sm text-muted-foreground">Nadie tiene este rol todavía.</CardContent></Card>;
  }
  return (
    <Card>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Usuario</TableHead>
              <TableHead>Alcance</TableHead>
              <TableHead className="w-32">Desde</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {assignments.map((a) => (
              <TableRow key={a.id}>
                <TableCell>
                  <p className="font-medium">{a.user?.full_name ?? '—'}</p>
                  <p className="text-xs text-muted-foreground">{a.user?.email}</p>
                </TableCell>
                <TableCell>
                  {SCOPE_LABEL[a.scope]}
                  {a.scope === 'DISTRICT' && a.district && <span className="text-muted-foreground"> · {a.district.name}</span>}
                  {a.scope === 'REGION' && a.region && <span className="text-muted-foreground"> · {a.region.name}</span>}
                </TableCell>
                <TableCell className="tabular-nums">{new Date(a.granted_at).toLocaleDateString('es-BO')}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
